// IT & Facilities — IT Asset Request Form (PDF page 17)
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { wsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  PageShell, PageHead, Card, Field, Input, Select, Textarea, Check, VFlow, Tabs, ReqStatus, Empty, Loading, Spin, Badge,
  useAsync, useProjects, useCostCenters, useQueryParam, fmtDate, today, BUSINESS_UNITS, LOCATIONS, DEPARTMENTS,
} from '../../components/ui';
import RequestDetail from '../../components/RequestDetail';
import { notify } from '../../utils/notify';

const blank = (u) => ({
  department: u?.businessUnit || u?.department || 'Water Business Unit', location: u?.baseLocation || 'Jamshedpur', requestType: 'New allocation', assetType: 'Laptop',
  requiredBy: '', project: u?.defaultProject || '', costCenter: u?.costCenter || '', justification: '',
  roleProfile: 'Engineering', ram: '16 GB', storage: '512 GB SSD', software: 'Approved CAD viewer, Office suite', accessories: 'Monitor, Keyboard, Mouse', policyAck: false,
});
const STEPS = [
  ['Manager justification', 'Request details and business need review.'],
  ['IT stock check', 'Check availability in IT inventory.'],
  ['Budget approval if purchase', 'Finance approval (if asset needs to be procured).'],
  ['Asset issue & acknowledgement', 'Asset allocation and employee acknowledgement.'],
];

export default function ITAssetRequest() {
  const { user } = useAuth();
  const projects = useProjects();
  const costCenters = useCostCenters();
  const openId = useQueryParam('id');
  const [f, setF] = useState(blank(user));
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(null);
  const [tab, setTab] = useState('mine');
  const [detail, setDetail] = useState(openId);
  const mine = useAsync(() => wsAPI.listRequests({ type: 'it_asset', scope: 'mine' }), []);
  const queue = useAsync(() => wsAPI.listRequests({ type: 'it_asset', scope: 'all' }), []);
  const me = useAsync(() => wsAPI.profile().then((d) => d.profile), [], { silent: true });
  useEffect(() => {
    if (me.data && !current) {
      setF((p) => ({
        ...p,
        department: me.data.businessUnit || me.data.department || p.department,
        location: me.data.baseLocation || p.location,
        project: p.project || me.data.defaultProject || '',
        costCenter: p.costCenter || me.data.costCenter || '',
      }));
    }
  }, [me.data]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (openId) setDetail(openId); }, [openId]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async (submit) => {
    if (submit) {
      const miss = [['requiredBy', 'Required by'], ['justification', 'Business justification'], ['software', 'Required software']].filter(([k]) => !String(f[k] || '').trim()).map(([, l]) => l);
      if (miss.length) { notify.error(`Please fill: ${miss.join(', ')}`); return; }
      if (!f.policyAck) { notify.error('Acknowledge the IT asset and acceptable-use policy'); return; }
    }
    setSaving(submit ? 'submit' : 'draft');
    try {
      const body = {
        type: 'it_asset', title: `${f.assetType} — ${f.requestType}`, project: f.project, costCenter: f.costCenter, department: f.department, location: f.location,
        requiredBy: f.requiredBy || undefined, data: { ...f }, submit,
      };
      const r = current ? await wsAPI.updateRequest(current._id, body) : await wsAPI.createRequest(body);
      setCurrent(r.request);
      notify.success(submit ? `${r.request.refNo} submitted — Manager justification next` : `Draft saved — ${r.request.refNo}`);
      mine.reload(); queue.reload();
      if (submit) { setCurrent(null); setF(blank(me.data || user)); }
    } catch (e) { notify.error(e.message || 'Could not save'); } finally { setSaving(null); }
  };

  const stageState = (i) => {
    if (!current || current.status === 'DRAFT') return i === 0 ? '' : 'ring';
    const s = current.stages?.[i];
    return { APPROVED: 'done', PENDING: 'current', SKIPPED: 'skipped', REJECTED: 'rejected', RETURNED: 'returned' }[s?.status] || 'ring';
  };
  const d = current?.data || {};
  const pendingForMe = (queue.data?.requests || []).filter((r) => r.canAct);
  const rows = tab === 'mine' ? (mine.data?.requests || []) : tab === 'queue' ? pendingForMe : (queue.data?.requests || []);

  // Stage-specific inputs for IT (stock check / asset issue)
  const stageFields = (r, sd, setSd) => {
    const st = r.stages[r.currentStage]?.key;
    if (st === 'it_stock') {
      return (
        <Field label="Stock availability">
          <Select value={sd.stockAvailable === undefined ? '' : sd.stockAvailable ? 'yes' : 'no'} onChange={(e) => setSd('stockAvailable', e.target.value === 'yes')} placeholder="Select" options={[{ value: 'yes', label: 'In stock — issue from inventory' }, { value: 'no', label: 'Purchase needed — budget approval' }]} />
        </Field>
      );
    }
    if (st === 'issue') {
      return (
        <>
          <Field label="Asset tag" required><Input value={sd.assetTag || ''} onChange={(e) => setSd('assetTag', e.target.value)} placeholder="e.g. TSE-LT-0042" /></Field>
          <Field label="Security setup"><Input value={sd.securitySetup || 'Encryption • MFA • Endpoint protection'} onChange={(e) => setSd('securitySetup', e.target.value)} /></Field>
        </>
      );
    }
    return null;
  };

  return (
    <PageShell section="IT & Facilities">
      <PageHead crumbs={[{ label: 'Home', to: '/dashboard' }, { label: 'IT & Facilities' }, { label: 'IT Asset Request' }]} title="IT Asset Request Form" subtitle={`Form ID: TSE-IT-FRM-AR-${new Date().getFullYear()}-001-R0 (Proposed)`}
        actions={current ? <Badge tone="info" lg>{current.refNo}</Badge> : null} />

      <div className="tse-grid g-3 mb-18" style={{ alignItems: 'start' }}>
        <Card title={<span><i className="bi bi-file-earmark-text" style={{ color: '#1479bd', marginRight: 10 }} />Request details</span>}>
          <div className="tse-stack" style={{ gap: 8 }}>
            <Field label="Employee" inline required><Input value={user?.userName || ''} readOnly /></Field>
            <Field label="Employee ID" inline><Input value={user?.employeeCode || ''} readOnly /></Field>
            <Field label="Department" inline required><Select value={f.department} onChange={(e) => set('department', e.target.value)} options={[...new Set([f.department, ...BUSINESS_UNITS, ...DEPARTMENTS].filter(Boolean))]} /></Field>
            <Field label="Location" inline required><Select value={f.location} onChange={(e) => set('location', e.target.value)} options={[...new Set([f.location, ...LOCATIONS].filter(Boolean))]} /></Field>
            <Field label="Request type" inline required><Select value={f.requestType} onChange={(e) => set('requestType', e.target.value)} options={['New allocation', 'Replacement', 'Upgrade', 'Repair', 'Return']} /></Field>
            <Field label="Asset type" inline required><Select value={f.assetType} onChange={(e) => set('assetType', e.target.value)} options={['Laptop', 'Desktop', 'Monitor', 'Mobile phone', 'Tablet', 'Printer', 'Software licence', 'Data card']} /></Field>
            <Field label="Required by" inline required><Input type="date" min={today()} value={f.requiredBy} onChange={(e) => set('requiredBy', e.target.value)} /></Field>
            <Field label="Project" inline><Select value={f.project} onChange={(e) => set('project', e.target.value)} placeholder="—" options={[...new Set([f.project, ...projects.map((p) => p.projectCode)].filter(Boolean))]} /></Field>
            <Field label="Cost center" inline><Select value={f.costCenter} onChange={(e) => set('costCenter', e.target.value)} placeholder="—" options={[...new Set([f.costCenter, ...costCenters.map((c) => c.code)].filter(Boolean))]} /></Field>
            <Field label="Business justification" required>
              <Textarea rows={3} maxLength={500} value={f.justification} onChange={(e) => set('justification', e.target.value)} placeholder="e.g. Project coordination and reporting." />
              <div className="tse-counter">{f.justification.length} / 500</div>
            </Field>
          </div>
        </Card>

        <Card title={<span><i className="bi bi-gear" style={{ color: '#1479bd', marginRight: 10 }} />Specifications</span>}>
          <div className="tse-stack" style={{ gap: 8 }}>
            <Field label="Role profile" required><Select value={f.roleProfile} onChange={(e) => set('roleProfile', e.target.value)} options={['Engineering', 'Design (CAD)', 'Office productivity', 'Site / field', 'Management']} /></Field>
            <Field label="RAM" required><Select value={f.ram} onChange={(e) => set('ram', e.target.value)} options={['8 GB', '16 GB', '32 GB', '64 GB', 'Not applicable']} /></Field>
            <Field label="Storage" required><Select value={f.storage} onChange={(e) => set('storage', e.target.value)} options={['256 GB SSD', '512 GB SSD', '1 TB SSD', 'Not applicable']} /></Field>
            <Field label="Required software" required><Select value={f.software} onChange={(e) => set('software', e.target.value)} options={['Approved CAD viewer, Office suite', 'Office suite', 'AutoCAD, Office suite', 'SAP client, Office suite', 'None']} /></Field>
            <Field label="Accessories"><Select value={f.accessories} onChange={(e) => set('accessories', e.target.value)} options={['Monitor, Keyboard, Mouse', 'Keyboard, Mouse', 'Headset', 'Docking station', 'None']} /></Field>
            <Check checked={f.policyAck} onChange={(v) => set('policyAck', v)} label="I acknowledge the IT asset and acceptable-use policy." />
            <Link to="/policies?q=IT%20Asset" className="tse-link" style={{ marginLeft: 30 }}>View policy <i className="bi bi-box-arrow-up-right" /></Link>
          </div>
        </Card>

        <div className="tse-stack" style={{ gap: 8 }}>
          <Card title={<span><i className="bi bi-diagram-3" style={{ color: '#1479bd', marginRight: 10 }} />Allocation and approvals</span>}>
            <VFlow items={STEPS.map(([t, s], i) => ({ mark: i + 1, state: stageState(i) || (i === 0 ? '' : 'ring'), title: t, sub: s }))} />
            <hr className="tse-divider" />
            <div className="tse-stack" style={{ gap: 8 }}>
              <Field label="Stock availability" inline><Input readOnly value={d.stockAvailable === true ? 'In stock' : d.stockAvailable === false ? 'Purchase needed' : 'Pending IT review'} /></Field>
              <Field label="Asset tag" inline><Input readOnly value={d.assetTag || 'Assigned on issue'} /></Field>
              <Field label="Security setup" inline><Input readOnly value={d.securitySetup || 'Encryption • MFA • Endpoint protection'} /></Field>
            </div>
          </Card>
          <div className="tse-row" style={{ gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="tse-btn lg outline" style={{ minWidth: 150 }} onClick={() => save(false)} disabled={!!saving}>{saving === 'draft' ? <Spin /> : null}Save draft</button>
            <button type="button" className="tse-btn lg" style={{ minWidth: 190 }} onClick={() => save(true)} disabled={!!saving}>{saving === 'submit' ? <Spin /> : null}Submit request</button>
          </div>
        </div>
      </div>

      <Card bodyClass="flush" title="IT asset requests" actions={<Tabs value={tab} onChange={setTab} tabs={[{ key: 'mine', label: 'My requests', count: (mine.data?.requests || []).length }, ...(['admin', 'manager', 'hod', 'finance'].includes(user?.role) ? [{ key: 'queue', label: 'Awaiting my action', count: pendingForMe.length }, { key: 'all', label: 'All I handle' }] : [])]} />}>
        {(tab === 'mine' ? mine.loading : queue.loading) ? <Loading /> : !rows.length ? <Empty icon="bi-laptop" title="No requests" /> : (
          <div className="tse-table-wrap"><table className="tse-table">
            <thead><tr><th>Request</th><th>Asset</th><th>Requester</th><th>Required by</th><th>Status</th><th>Asset tag</th><th /></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r._id} className="clickable" onClick={() => (r.isOwner && ['DRAFT', 'RETURNED'].includes(r.status) ? (setCurrent(r), setF({ ...blank(user), ...r.data })) : setDetail(r._id))}>
                <td className="strong">{r.refNo}</td><td>{r.title}</td><td>{r.requesterName}</td><td>{fmtDate(r.requiredBy)}</td><td><ReqStatus request={r} /></td><td>{r.data?.assetTag || '—'}</td>
                <td onClick={(e) => e.stopPropagation()}><button type="button" className="tse-btn xs outline" onClick={() => setDetail(r._id)}>{r.canAct ? 'Review' : 'Details'}</button></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </Card>
      <RequestDetail id={detail} onClose={() => setDetail(null)} onChanged={() => { mine.reload(); queue.reload(); }} stageFields={stageFields} />
    </PageShell>
  );
}
